import React, { useEffect, useMemo, useReducer, useState } from 'react'
import Flatpickr from 'react-flatpickr'
import { Portuguese } from 'flatpickr/dist/l10n/pt'
import English from 'flatpickr/dist/l10n/default'
import * as Icon from 'react-feather'
import { Link } from 'react-router-dom'
import useAxios from 'axios-hooks'
import { format, subDays } from 'date-fns'
import { Filter, Calendar } from 'react-feather'
import _ from 'lodash'

import { Container, Header, RowContainer, DatePickerContainer } from './styles'
import MultiLineChartCard from '~/components/MultiLineChartCard'
import Breadcrumb from '~/components/Breadcrumb'
import FilterPanel from '~/components/InsightsFilterPanel'
import { useSelector } from 'react-redux'
import { Button, Spinner } from 'reactstrap'
import styled from 'styled-components'
import { shade } from 'polished'
import { Hide, Show } from '~/components/commons'
import { FormattedMessage, useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { LocationOverviewGrid } from './components/LocationOverviewGrid'
import { LegacyInsightsCard } from './components/LegacyInsightsCard'
import { InsightsOverview } from './components/InsightsOverview'

import { queries } from '~/constants/mediaQuery'
import { getDateUTCZeroHours } from '~/helpers/getDateUTCZeroHours'
import { withDefault } from 'use-query-params'
import {
  SearchParamsHandlerProvider,
  useSearchParamsHandler,
} from '~/contexts/FilterContext'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import FilterButton from '~/components/FilterButton'
import DefaultFilterHandler from '~/components/Filters/Default/Handler'

const DEFAULT_END_SUB_DATE = 10
const DEFAULT_START_SUB_DATE = 43

const initialState = {
  project: [],
  state: [],
  city: [],
  locationArea: [],
  location: [],
  label: [],
  hasChanged: false,
  dateChanged: false,
  date: { start: '', end: '' },
}

const DEFAULT_STATE = {
  calls: {
    name: 'Call you',
    data: [],
  },
  routes: {
    name: 'Request Directions',
    data: [],
  },
  visits: {
    name: 'Website visits',
    data: [],
  },
  queries: {
    direct: {
      name: 'Direct',
    },
    indirect: {
      name: 'Discovery',
    },
    total: {
      name: 'Total',
    },
  },
  actionsOverview: [],
  questionsOverview: {
    meta: {
      status: {
        answered: 0,
        notAnswered: 0,
      },
    },
  },
  reviewsOverview: {
    meta: {
      status: {
        answered: 0,
        notAnswered: 0,
      },
    },
  },
}

const filterReducer = (state, action) => {
  switch (action.type) {
    case '@filter/SET_PROPERTY': {
      return {
        ...state,
        hasChanged: true,
        dateChanged: false,
        [action.payload.property]: action.payload.value,
      }
    }
    case '@filter/SET_DATE_PROPERTY': {
      return {
        ...state,
        hasChanged: true,
        dateChanged: true,
        [action.payload.property]: action.payload.value,
      }
    }
    case '@filter/APPLY': {
      return { ...state, hasChanged: false }
    }
    case '@filter/RESET': {
      return { ...initialState }
    }
    default: {
      return state
    }
  }
}

function getFilteredLocationsNames(state) {
  const notEmptyFilters = _.pickBy(
    state,
    filter => _.isArray(filter) && !_.isEmpty(filter)
  )

  if (_.isEmpty(notEmptyFilters)) {
    return []
  }

  const result = Object.values(notEmptyFilters).reduce((acc, value) => {
    return acc.concat(value)
  }, [])

  return _.uniq(result)
}

const metrics = [
  'ACTIONS_DRIVING_DIRECTIONS',
  'ACTIONS_PHONE',
  'ACTIONS_WEBSITE',
  'LOCAL_POST_VIEWS_SEARCH',
  'LOCAL_POST_ACTIONS_CALL_TO_ACTION',
  'QUERIES_CHAIN',
  'QUERIES_DIRECT',
  'QUERIES_INDIRECT',
  'QUERIES_TOTAL',
  'VIEWS_MAPS',
  'VIEWS_SEARCH',
  'VIEWS_TOTAL',
  'PHOTOS_COUNT_MERCHANT',
  'PHOTOS_COUNT_CUSTOMERS',
  'PHOTOS_VIEWS_MERCHANT',
  'PHOTOS_VIEWS_CUSTOMERS',
]

const dmMetrics = [
  'BUSINESS_IMPRESSIONS_DESKTOP_MAPS',
  'BUSINESS_IMPRESSIONS_DESKTOP_SEARCH',
  'BUSINESS_IMPRESSIONS_MOBILE_MAPS',
  'BUSINESS_IMPRESSIONS_MOBILE_SEARCH',
  'BUSINESS_IMPRESSIONS_TOTAL_MAPS',
  'BUSINESS_IMPRESSIONS_TOTAL_SEARCH',
  'BUSINESS_DIRECTION_REQUESTS',
  'CALL_CLICKS',
  'WEBSITE_CLICKS',
]

const timeRange = {
  startDate: getDateUTCZeroHours({ subdays: DEFAULT_START_SUB_DATE }),
  endDate: getDateUTCZeroHours({
    endOfDay: true,
    subdays: DEFAULT_END_SUB_DATE,
  }),
}

function Insights() {
  const [state, dispatch] = useReducer(filterReducer, initialState)
  const projectId = useSelector(state => state.user?.selectedProject?.id)
  const projectName = useSelector(state => state.user?.selectedProject?.name)
  const selectedProject = useSelector(state => state.user?.selectedProject)
  const gmbAccountId = useSelector(
    state => state.user?.selectedProject?.gmbAccountId
  )
  const legacyInsights = useSelector(
    state => state.user?.profile?.account?.legacyInsights
  )
  const [fetchingCSV, setFetchingCSV] = useState(false)
  const [intervalRange, setIntervalRange] = useState(
    selectedProject.id === '7f77bb82-2e71-4848-b13b-be245cab9cda'
      ? {
          startDate: '2024-07-01T00:00:00.000Z',
          endDate: '2024-07-31T23:59:59.999Z',
        }
      : timeRange
  )
  const [showFilter, setShowFilter] = useState(false)
  const [showOldReports, setShowOldReports] = useState(false)
  const { query } = useSearchParamsHandler()
  const [filterOn, setFilterOn] = React.useState(false)

  const filterCount = getDefaultFiltersCount({
    states: query.states,
    cities: query.cities,
    areas: query.areas,
    labels: query.labels,
    locations: query.locations,
  })

  const intl = useIntl()

  const [{ data, error, loading: chartDataLoading }, revalidate] = useAxios(
    {
      url: `/projects/${projectId}/locations/reportInsights:aggregated`,
      method: 'POST',
      params: { ...intervalRange, ...query },
      data: {
        metrics,
        locationNames: [],
      },
    },
    {
      useCache: true,
    }
  )

  const [
    {
      data: dailyMetrics,
      error: dailyMetricsError,
      loading: dailyMetricsLoading,
    },
    revalidateDailyMetrics,
  ] = useAxios(
    {
      url: `/projects/${projectId}/locations/dailyMetrics:aggregated`,
      method: 'POST',
      params: { ...intervalRange, ...query },
      data: {
        metrics: dmMetrics,
        locationNames: [],
      },
    },
    {
      useCache: true,
    }
  )

  const [, downloadInsightsCsvFile] = useAxios(
    {
      url: `/projects/${projectId}/reportInsightsCSV`,
      method: 'POST',
      params: intervalRange,
      data: {
        locationNames: [],
        gmbAccountId,
      },
    },
    { useCache: true, manual: true }
  )

  const [, downloadDmCsvFile] = useAxios(
    {
      url: `/projects/${projectId}/dailyMetricsCSV`,
      method: 'POST',
      params: intervalRange,
      data: {
        locationNames: [],
        gmbAccountId,
      },
    },
    { useCache: true, manual: true }
  )

  const [
    { data: reviewsOverview, loading: loadingReviews },
    refetchReviewOverview,
  ] = useAxios(
    {
      url: `/projects/${projectId}/reviews/overview`,
      method: 'POST',
      params: {
        ...query,
      },
      data: {
        timeRange: intervalRange,
      },
    },
    { useCache: true }
  )

  const [
    { data: questionsOverview, loading: loadingQuestions },
    refetchQuestionsOverview,
  ] = useAxios(
    {
      url: `/projects/${projectId}/questions/overview`,
      method: 'POST',
      params: {
        ...query,
      },
      data: {
        timeRange: intervalRange,
      },
    },
    { useCache: true }
  )

  const [
    { data: npsOverview, loading: loadingNps },
    refetchNpsOverview,
  ] = useAxios(
    {
      url: `/projects/${projectId}/nps/overview`,
      method: 'POST',
      params: {
        ...query,
      },
      data: {
        timeRange: intervalRange,
      },
    },
    { useCache: true }
  )

  const getIntervalDates = (startDate, endDate, formatString) => {
    const interval = []
    let currentDate = startDate
    // eslint-disable-next-line no-extend-native
    Date.prototype.addDays = function (days) {
      const date = new Date(this.valueOf())
      date.setDate(date.getDate() + days)
      return date
    }
    while (currentDate <= endDate) {
      const formattedDate = formatString
        ? format(currentDate, formatString)
        : currentDate.toISOString()
      interval.push(formattedDate)
      currentDate = currentDate.addDays(1)
    }
    return interval
  }

  const getRatingAverage = ratingsAvg => {
    const ratingSum = Object.values(ratingsAvg)?.reduce(
      (acc, cur) => acc + cur,
      0
    )
    const ratingLength = Object.values(ratingsAvg)?.length

    const average = ratingLength === 0 ? 0 : ratingSum / ratingLength
    const averageFixed = average.toFixed(1)

    return averageFixed
  }

  const getRatingAverageByCategory = ratingsAvg => {
    return Object.entries(ratingsAvg ?? {})?.map(([key, value]) => {
      return {
        x: key,
        y: value,
      }
    })
  }

  const populateDataByInterval = React.useCallback(
    (seriesData, formatString) => {
      const removeUTC = date => date.replace('Z', '')
      const startTime = intervalRange.startDate
      const endTime = intervalRange.endDate

      const endDate = new Date(removeUTC(endTime))
      const startDate = new Date(removeUTC(startTime))
      const interval = getIntervalDates(startDate, endDate, formatString)

      return interval.map(date => ({
        x: date,
        y: seriesData[date] || 0,
      }))
    },
    [intervalRange]
  )

  const handleShowFilter = React.useCallback(() => {
    setShowFilter(prev => !prev)
  }, [])

  const chartData = useMemo(() => {
    if (
      showOldReports &&
      (!data ||
        !reviewsOverview ||
        !questionsOverview ||
        error ||
        chartDataLoading)
    ) {
      return DEFAULT_STATE
    }
    if (
      !showOldReports &&
      (!dailyMetrics ||
        !reviewsOverview ||
        !questionsOverview ||
        dailyMetricsError ||
        dailyMetricsLoading)
    ) {
      return DEFAULT_STATE
    }

    return {
      calls: {
        total: data?.ACTIONS_PHONE?.AGGREGATED_TOTAL ?? 0,
        name: 'Call you',
        data: Object.keys(data?.ACTIONS_PHONE?.AGGREGATED_DAILY ?? {}).map(
          key => ({
            x: key,
            y: data?.ACTIONS_PHONE?.AGGREGATED_DAILY[key],
          })
        ),
      },
      visits: {
        total: data?.ACTIONS_WEBSITE?.AGGREGATED_TOTAL ?? 0,
        name: 'Website visits',
        data: Object.keys(data?.ACTIONS_WEBSITE?.AGGREGATED_DAILY ?? {}).map(
          key => ({
            x: key,
            y: data?.ACTIONS_WEBSITE?.AGGREGATED_DAILY[key],
          })
        ),
      },
      routes: {
        total: data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_TOTAL ?? 0,
        name: 'Request Directions',
        data: Object.keys(
          data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_DAILY ?? {}
        ).map(key => ({
          x: key,
          y: data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_DAILY[key],
        })),
      },
      queries: {
        total: data?.VIEWS_TOTAL?.AGGREGATED_TOTAL ?? 0,
        options: {
          labels: ['Direct', 'Discovery', 'Branded', 'Total Views'],
          colors: ['#C870FF', '#28C76F', '#C78728', '#2C2C2C'],
          dashArray: [0, 0, 0, 10],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'Direct' }),
            total: data?.QUERIES_DIRECT?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(data?.QUERIES_DIRECT?.AGGREGATED_DAILY ?? {}).map(
              key => ({
                x: key,
                y: data?.QUERIES_DIRECT?.AGGREGATED_DAILY[key],
              })
            ),
          },
          {
            name: intl.formatMessage({ id: 'Discovery' }),
            total: data?.QUERIES_INDIRECT?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.QUERIES_INDIRECT?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.QUERIES_INDIRECT?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'Branded' }),
            total: data?.QUERIES_CHAIN?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(data?.QUERIES_CHAIN?.AGGREGATED_DAILY ?? {}).map(
              key => ({
                x: key,
                y: data?.QUERIES_CHAIN?.AGGREGATED_DAILY[key],
              })
            ),
          },
          {
            name: intl.formatMessage({ id: 'Total' }),
            total: data?.QUERIES_TOTAL?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.QUERIES_INDIRECT?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                data?.QUERIES_INDIRECT?.AGGREGATED_DAILY[key] +
                data?.QUERIES_DIRECT?.AGGREGATED_DAILY[key] +
                data?.QUERIES_CHAIN?.AGGREGATED_DAILY[key],
            })),
          },
        ],
      },
      queriesOverview: {
        options: {
          labels: ['Direct', 'Discovery'],
          colors: ['#C870FF', '#EFCB70'],
        },
        series: [
          data?.QUERIES_DIRECT?.AGGREGATED_TOTAL ?? 0,
          data?.QUERIES_INDIRECT?.AGGREGATED_TOTAL ?? 0,
        ],
      },
      views: {
        total: data?.VIEWS_TOTAL?.AGGREGATED_TOTAL ?? 0,
        options: {
          labels: ['Google Search', 'Google Maps', 'Total Views'],
          colors: ['#70C3FF', '#745FF9', '#333'],
          dashArray: [0, 0, 10],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'Google Search' }),
            total: data?.VIEWS_SEARCH?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(data?.VIEWS_SEARCH?.AGGREGATED_DAILY ?? {}).map(
              key => ({
                x: key,
                y: data?.VIEWS_SEARCH?.AGGREGATED_DAILY[key],
              })
            ),
          },
          {
            name: intl.formatMessage({ id: 'Maps' }),
            total: data?.VIEWS_MAPS?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(data?.VIEWS_MAPS?.AGGREGATED_DAILY ?? {}).map(
              key => ({
                x: key,
                y: data?.VIEWS_MAPS?.AGGREGATED_DAILY[key],
              })
            ),
          },
          {
            name: intl.formatMessage({ id: 'Total' }),
            total: data?.VIEWS_TOTAL?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(data?.VIEWS_TOTAL?.AGGREGATED_DAILY ?? {}).map(
              key => ({
                x: key,
                y: data?.VIEWS_TOTAL?.AGGREGATED_DAILY[key],
              })
            ),
          },
        ],
      },
      viewsOverview: {
        title: 'Views on Google',
        data: [
          data?.VIEWS_SEARCH?.AGGREGATED_TOTAL ?? 0,
          data?.VIEWS_MAPS?.AGGREGATED_TOTAL ?? 0,
        ],
      },
      actionsOverview: [
        data?.ACTIONS_PHONE?.AGGREGATED_TOTAL ?? 0,
        data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_TOTAL ?? 0,
        data?.ACTIONS_WEBSITE?.AGGREGATED_TOTAL ?? 0,
      ],
      aggregatedDaily: {
        options: {
          labels: ['Website', 'Directions', 'Calls', 'Total Views'],
          colors: ['#FF1B7A', '#FFA84C', '#8C8C8C', '#2C2C2C'],
          dashArray: [0, 0, 0, 10],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'Website' }),
            total: data?.ACTIONS_WEBSITE?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.ACTIONS_WEBSITE?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.ACTIONS_WEBSITE?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'Directions' }),
            total: data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'Calls' }),
            total: data?.ACTIONS_PHONE?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(data?.ACTIONS_PHONE?.AGGREGATED_DAILY ?? {}).map(
              key => ({
                x: key,
                y: data?.ACTIONS_PHONE?.AGGREGATED_DAILY[key],
              })
            ),
          },
          {
            name: intl.formatMessage({ id: 'Total' }),
            total:
              data?.ACTIONS_WEBSITE?.AGGREGATED_TOTAL +
                data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_TOTAL +
                data?.ACTIONS_PHONE?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.ACTIONS_WEBSITE?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                data?.ACTIONS_WEBSITE?.AGGREGATED_DAILY[key] +
                data?.ACTIONS_DRIVING_DIRECTIONS?.AGGREGATED_DAILY[key] +
                data?.ACTIONS_PHONE?.AGGREGATED_DAILY[key],
            })),
          },
        ],
      },
      reviewsAverage: {
        options: {
          chart: {
            type: 'bar',
          },
          legend: {
            show: false,
          },
          tooltip: {
            y: {
              formatter: value => value.toFixed(2),
            },
          },
          colors: ['#7E72F2'],
          grid: {
            row: {
              colors: ['#f3f3f3', 'transparent'],
              opacity: 0.5,
            },
          },
          fill: {
            colors: ['#7E72F2'],
            type: 'solid',
            opacity: 1,
          },
          plotOptions: {
            bar: {
              borderRadius: 5,
              borderRadiusApplication: 'around',
              borderRadiusWhenStacked: 'all',
              barHeight: '100%',
            },
          },
          yaxis: {
            decimalsInFloat: 0,
            labels: {
              formatter: value => Math.floor(value),
            },
            max: 5,
            tickAmount: 5,
          },
          xaxis: {
            type: 'category',
          },
          dataLabels: {
            enabled: false,
          },
        },
        data: [
          {
            name: intl.formatMessage({ id: 'Range Average' }),
            total: getRatingAverage(reviewsOverview?.ratingAvg ?? {}),
            data: populateDataByInterval(
              reviewsOverview?.ratingAvg,
              'yyyy-MM-dd'
            ),
          },
        ],
      },
      reviewsCategory: {
        data: [
          {
            name: intl.formatMessage({ id: 'Reviews' }),
            data: getRatingAverageByCategory(
              reviewsOverview?.ratingCount ?? {}
            ),
          },
        ],
      },
      questionsAndReviews: {
        options: {
          labels: ['New Reviews', 'New Questions'],
          colors: ['#EDC223', '#FF3B8E'],
          dashArray: [0, 0],
        },
        reviewsMeta: {
          status: {
            answered: reviewsOverview?.meta?.status?.answered,
            notAnswered: reviewsOverview?.meta?.status?.notAnswered,
          },
        },
        questionMeta: {
          status: {
            answered: questionsOverview?.meta?.status?.answered,
            notAnswered: questionsOverview?.meta?.status?.notAnswered,
          },
        },
        data: [
          {
            name: intl.formatMessage({ id: 'New Reviews' }),
            total:
              reviewsOverview?.meta?.status?.answered +
                reviewsOverview?.meta?.status?.notAnswered ?? 0,
            comparisonTotal:
              reviewsOverview?.meta?.comparisonStatus?.answered +
                reviewsOverview?.meta?.comparisonStatus?.notAnswered ?? 0,
            data: populateDataByInterval(
              reviewsOverview?.reviews,
              'yyyy-MM-dd'
            ),
          },
          {
            name: intl.formatMessage({ id: 'New Questions' }),
            total:
              questionsOverview?.meta?.status?.answered +
                questionsOverview?.meta?.status?.notAnswered ?? 0,
            comparisonTotal:
              questionsOverview?.meta?.comparisonStatus?.answered +
                questionsOverview?.meta?.comparisonStatus?.notAnswered ?? 0,
            data: populateDataByInterval(
              questionsOverview?.questions,
              'yyyy-MM-dd'
            ),
          },
        ],
      },
      posts: {
        options: {
          labels: ['Posts views', 'Posts actions'],
          colors: ['#28C76F', '#C870FF'],
          dashArray: [0, 0],
        },
        reviewsMeta: {
          status: {
            answered: reviewsOverview?.meta?.status?.answered,
            notAnswered: reviewsOverview?.meta?.status?.notAnswered,
          },
        },
        questionMeta: {
          status: {
            answered: questionsOverview?.meta?.status?.answered,
            notAnswered: questionsOverview?.meta?.status?.notAnswered,
          },
        },
        data: [
          {
            name: intl.formatMessage({ id: 'Posts views' }),
            total: data?.LOCAL_POST_VIEWS_SEARCH?.AGGREGATED_TOTAL ?? 0,

            data: Object.keys(
              data?.LOCAL_POST_VIEWS_SEARCH?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.LOCAL_POST_VIEWS_SEARCH?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'Posts actions' }),
            total:
              data?.LOCAL_POST_ACTIONS_CALL_TO_ACTION?.AGGREGATED_TOTAL ?? 0,

            data: Object.keys(
              data?.LOCAL_POST_ACTIONS_CALL_TO_ACTION?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.LOCAL_POST_ACTIONS_CALL_TO_ACTION?.AGGREGATED_DAILY[key],
            })),
          },
        ],
      },
      photosUploaded: {
        options: {
          labels: [
            'Photos by customers',
            'Photos by merchant',
            'Views on customers photos',
            'Views on merchant photos',
          ],
          colors: ['#28C76F', '#70C3FF', '#FFA84C', '#FF3B8E'],
          dashArray: [0, 0, 0, 0],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'Photos by customers' }),
            total: data?.PHOTOS_COUNT_CUSTOMERS?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.PHOTOS_COUNT_CUSTOMERS?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.PHOTOS_COUNT_CUSTOMERS?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'Photos by merchant' }),
            total: data?.PHOTOS_COUNT_MERCHANT?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.PHOTOS_COUNT_MERCHANT?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.PHOTOS_COUNT_MERCHANT?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'Views on customers photos' }),
            total: data?.PHOTOS_VIEWS_CUSTOMERS?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.PHOTOS_VIEWS_CUSTOMERS?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.PHOTOS_VIEWS_CUSTOMERS?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'Views on merchant photos' }),
            total: data?.PHOTOS_VIEWS_MERCHANT?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              data?.PHOTOS_VIEWS_MERCHANT?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: data?.PHOTOS_VIEWS_MERCHANT?.AGGREGATED_DAILY[key],
            })),
          },
        ],
      },
      searchViews: {
        total:
          dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_SEARCH?.AGGREGATED_TOTAL ??
          0,
        comparisonTotal:
          dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_SEARCH
            ?.AGGREGATED_COMPARISON_TOTAL ?? 0,
        options: {
          labels: [
            'insights.search.subtitle.1',
            'insights.search.subtitle.2',
            'insights.search.subtitle.3',
          ],
          colors: ['#28C76F', '#C870FF', '#333'],
          dashArray: [0, 0, 10],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'insights.search.subtitle.1' }),
            total:
              dailyMetrics?.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH
                ?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH
                ?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_IMPRESSIONS_DESKTOP_SEARCH
                  ?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.search.subtitle.2' }),
            total:
              dailyMetrics?.BUSINESS_IMPRESSIONS_MOBILE_SEARCH
                ?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_IMPRESSIONS_MOBILE_SEARCH
                ?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_IMPRESSIONS_MOBILE_SEARCH
                  ?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.search.subtitle.3' }),
            total:
              dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_SEARCH
                ?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_SEARCH
                ?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_SEARCH
                  ?.AGGREGATED_DAILY[key],
            })),
          },
        ],
      },
      mapsViews: {
        total:
          dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_MAPS?.AGGREGATED_TOTAL ?? 0,
        comparisonTotal:
          dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_MAPS
            ?.AGGREGATED_COMPARISON_TOTAL ?? 0,
        options: {
          labels: [
            'insights.maps.subtitle.1',
            'insights.maps.subtitle.2',
            'insights.maps.subtitle.3',
          ],
          colors: ['#28C76F', '#C870FF', '#333'],
          dashArray: [0, 0, 10],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'insights.maps.subtitle.1' }),
            total:
              dailyMetrics?.BUSINESS_IMPRESSIONS_DESKTOP_MAPS
                ?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_IMPRESSIONS_DESKTOP_MAPS
                ?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_IMPRESSIONS_DESKTOP_MAPS
                  ?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.maps.subtitle.2' }),
            total:
              dailyMetrics?.BUSINESS_IMPRESSIONS_MOBILE_MAPS
                ?.AGGREGATED_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_IMPRESSIONS_MOBILE_MAPS
                ?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_IMPRESSIONS_MOBILE_MAPS
                  ?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.maps.subtitle.3' }),
            total:
              dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_MAPS?.AGGREGATED_TOTAL ??
              0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_MAPS?.AGGREGATED_DAILY ??
                {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_IMPRESSIONS_TOTAL_MAPS?.AGGREGATED_DAILY[
                  key
                ],
            })),
          },
        ],
      },
      customerActions: {
        total:
          dailyMetrics?.BUSINESS_DIRECTION_REQUESTS?.AGGREGATED_TOTAL +
            dailyMetrics?.CALL_CLICKS?.AGGREGATED_TOTAL +
            dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_TOTAL ?? 0,
        comparisonTotal:
          dailyMetrics?.BUSINESS_DIRECTION_REQUESTS
            ?.AGGREGATED_COMPARISON_TOTAL +
            dailyMetrics?.CALL_CLICKS?.AGGREGATED_COMPARISON_TOTAL +
            dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_COMPARISON_TOTAL ?? 0,
        options: {
          labels: [
            'insights.actions.subtitle.1',
            'insights.actions.subtitle.2',
            'insights.actions.subtitle.3',
            'insights.actions.subtitle.4',
          ],
          colors: ['#70C3FF', '#745FF9', '#C78728', '#333'],
          dashArray: [0, 0, 0, 10],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'insights.actions.subtitle.1' }),
            total:
              dailyMetrics?.BUSINESS_DIRECTION_REQUESTS?.AGGREGATED_TOTAL ?? 0,
            comparisonTotal:
              dailyMetrics?.BUSINESS_DIRECTION_REQUESTS
                ?.AGGREGATED_COMPARISON_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_DIRECTION_REQUESTS?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_DIRECTION_REQUESTS?.AGGREGATED_DAILY[
                  key
                ],
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.actions.subtitle.2' }),
            total: dailyMetrics?.CALL_CLICKS?.AGGREGATED_TOTAL ?? 0,
            comparisonTotal:
              dailyMetrics?.CALL_CLICKS?.AGGREGATED_COMPARISON_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.CALL_CLICKS?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: dailyMetrics?.CALL_CLICKS?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.actions.subtitle.3' }),
            total: dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_TOTAL ?? 0,
            comparisonTotal:
              dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_COMPARISON_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y: dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_DAILY[key],
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.actions.subtitle.4' }),
            total:
              dailyMetrics?.BUSINESS_DIRECTION_REQUESTS?.AGGREGATED_TOTAL +
                dailyMetrics?.CALL_CLICKS?.AGGREGATED_TOTAL +
                dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_TOTAL ?? 0,
            comparisonTotal:
              dailyMetrics?.BUSINESS_DIRECTION_REQUESTS
                ?.AGGREGATED_COMPARISON_TOTAL +
                dailyMetrics?.CALL_CLICKS?.AGGREGATED_COMPARISON_TOTAL +
                dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_COMPARISON_TOTAL ?? 0,
            data: Object.keys(
              dailyMetrics?.BUSINESS_DIRECTION_REQUESTS?.AGGREGATED_DAILY ?? {}
            ).map(key => ({
              x: key,
              y:
                dailyMetrics?.BUSINESS_DIRECTION_REQUESTS?.AGGREGATED_DAILY[
                  key
                ] +
                dailyMetrics?.CALL_CLICKS?.AGGREGATED_DAILY[key] +
                dailyMetrics?.WEBSITE_CLICKS?.AGGREGATED_DAILY[key],
            })),
          },
        ],
      },
      nps: {
        total: npsOverview?.meta?.total ?? 0,
        comparisonTotal: npsOverview?.meta?.total ?? 0,
        options: {
          labels: [
            'insights.nps.subtitle.1',
            'insights.nps.subtitle.2',
            'insights.nps.subtitle.3',
            'insights.nps.subtitle.4',
          ],
          colors: ['#333', '#34C38F', '#EDC223', '#FF4C51'],
          dashArray: [10, 0, 0, 0],
        },
        data: [
          {
            name: intl.formatMessage({ id: 'insights.nps.subtitle.1' }),
            total: npsOverview?.meta?.total ?? 0,
            data: npsOverview?.data.map(([key, data]) => ({
              x: key,
              y: data.promoters + data.passives + data.detractors,
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.nps.subtitle.2' }),
            total: npsOverview?.meta?.promoters ?? 0,
            data: npsOverview?.data.map(([key, data]) => ({
              x: key,
              y: data.promoters,
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.nps.subtitle.3' }),
            total: npsOverview?.meta?.passives ?? 0,
            data: npsOverview?.data.map(([key, data]) => ({
              x: key,
              y: data.passives,
            })),
          },
          {
            name: intl.formatMessage({ id: 'insights.nps.subtitle.4' }),
            total: npsOverview?.meta?.detractors ?? 0,
            data: npsOverview?.data.map(([key, data]) => ({
              x: key,
              y: data.detractors,
            })),
          },
        ],
      },
    }
  }, [
    data,
    reviewsOverview,
    questionsOverview,
    error,
    dailyMetrics,
    showOldReports,
    dailyMetricsLoading,
    dailyMetricsError,
    chartDataLoading,
    populateDataByInterval,
    intl,
  ])

  const refetch = () => {
    if (!state.hasChanged) {
      return
    }

    const locationNames = getFilteredLocationsNames(state)
    const timeRange = {
      startDate: state?.date?.start || getDateUTCZeroHours({ subdays: 33 }),
      endDate:
        state?.date?.end || getDateUTCZeroHours({ subdays: 3, endOfDay: true }),
    }

    setIntervalRange(timeRange)

    refetchReviewOverview({
      data: {
        locationNames,
        timeRange,
      },
    })

    refetchQuestionsOverview({
      data: {
        locationNames,
        timeRange,
      },
    })

    refetchNpsOverview({
      data: {
        locationNames,
        timeRange,
      },
    })

    if (legacyInsights) {
      revalidate({
        params: {
          startDate: state?.date?.start || timeRange.startDate,
          endDate: state?.date?.end || timeRange.endDate,
        },
        data: {
          metrics,
          locationNames,
        },
      })
    }

    revalidateDailyMetrics({
      params: {
        startDate: state?.date?.start || timeRange.startDate,
        endDate: state?.date?.end || timeRange.endDate,
      },
      data: {
        dmMetrics,
        locationNames,
      },
    })

    dispatch({
      type: '@filter/APPLY',
    })

    if (showFilter) handleShowFilter()
  }

  const getFileName = (locationNames = []) => {
    const quantity = locationNames.length ? `[${locationNames.length}]` : ''
    const date = format(
      new Date(),
      intl.locale === 'en' ? 'MM-d-yyyy' : 'd-MM-yyyy'
    )
    return `${projectName}${quantity}-${date}.csv`
  }

  const defaultEnd =
    selectedProject.id === '7f77bb82-2e71-4848-b13b-be245cab9cda'
      ? '2024-07-01'
      : format(subDays(new Date(), DEFAULT_END_SUB_DATE), 'yyyy-MM-dd')
  const defaultStart =
    selectedProject.id === '7f77bb82-2e71-4848-b13b-be245cab9cda'
      ? '2024-07-31'
      : format(subDays(new Date(), DEFAULT_START_SUB_DATE), 'yyyy-MM-dd')

  const getDateUTCZeroHours = ({ date, subdays = 0, endOfDay = false }) => {
    const d = date ? new Date(date) : new Date()
    const utcDateZeroHours = new Date(
      Date.UTC(
        d.getFullYear(),
        d.getMonth(),
        d.getDate() - subdays,
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0
      )
    ).toISOString()
    return utcDateZeroHours
  }

  const downloadCSV = async () => {
    setFetchingCSV(true)
    const locationNames = getFilteredLocationsNames(state)
    const downloadCsv = showOldReports
      ? downloadInsightsCsvFile
      : downloadDmCsvFile
    downloadCsv({ data: { gmbAccountId, locationNames } })
      .then(({ data }) => {
        const blob = new Blob([data], { type: 'data:text/csv;charset=utf-8,' })
        const blobURL = window.URL.createObjectURL(blob)
        // Create new tag for download file
        const anchor = document.createElement('a')
        anchor.download = getFileName(locationNames)
        anchor.href = blobURL
        anchor.dataset.downloadurl = [
          'text/csv',
          anchor.download,
          anchor.href,
        ].join(':')
        anchor.click()
        // Remove URL.createObjectURL. The browser should not save the reference to the file.
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          URL.revokeObjectURL(blobURL)
        }, 100)
      })
      .catch(e => {
        toast.error('Error on downloading your CSV file.')
        console.error(e)
      })
      .finally(() => setFetchingCSV(false))
  }

  useEffect(() => {
    dispatch({
      type: '@filter/RESET',
    })
  }, [projectId])

  useEffect(() => {
    if (state?.hasChanged && state?.dateChanged) {
      setTimeout(() => refetch(), 1000)
    }
    // eslint-disable-next-line
  }, [state])

  return (
    <Container>
      <Header>
        <BreadcrumbContainer>
          <Breadcrumb
            paths={[
              {
                render: () => (
                  <Link to="/">
                    <Icon.Home size={20} />
                  </Link>
                ),
              },
              { text: 'Insights' },
            ]}
          />
          <div className="insights-menu-buttons">
            <CreateButton onClick={() => handleShowFilter()}>
              <Filter size={14} />
              <FormattedMessage id="Filters" />
            </CreateButton>
            <CreateButton onClick={() => downloadCSV()} disabled={fetchingCSV}>
              <Show condition={fetchingCSV}>
                <Spinner
                  color="white"
                  size="sm"
                  className="reload-spinner mr-1"
                />
              </Show>
              <Hide condition={fetchingCSV}>
                <Icon.Download size={14} />
              </Hide>
              <FormattedMessage id=".CSV" />
            </CreateButton>
          </div>
        </BreadcrumbContainer>
        <FilterPanel
          filtersState={state}
          dispatch={dispatch}
          onApply={refetch}
          showRating={false}
          hiddenFilters={!showFilter}
        />
      </Header>

      <HeroSessionContent>
        <h2>
          <FormattedMessage id="General Insight" />
        </h2>
        <div className="buttons">
          <div className="d-flex align-items-center " style={{ gap: '0.7em' }}>
            <Calendar size={25} />
            <DatePickerContainer>
              <Flatpickr
                className="form-control"
                style={{
                  height: '100%',
                }}
                options={{
                  mode: 'range',
                  defaultDate: [defaultStart, defaultEnd],
                  maxDate: subDays(new Date(), DEFAULT_END_SUB_DATE),
                  animate: true,
                  altInput: true,
                  altFormat: 'M d',
                  dateFormat: 'Y-m-d',
                  locale: intl.locale === 'br' ? Portuguese : English,
                }}
                onChange={value => {
                  const [start, end] = value

                  if (end) {
                    dispatch({
                      type: '@filter/SET_DATE_PROPERTY',
                      payload: {
                        property: 'date',
                        value: {
                          start: getDateUTCZeroHours({ date: start }),
                          end: getDateUTCZeroHours({
                            date: end,
                            endOfDay: true,
                          }),
                        },
                      },
                    })
                  }
                }}
              />
            </DatePickerContainer>
          </div>
          <FilterButton
            filterCount={filterCount}
            isActive={filterOn}
            onClick={() => setFilterOn(filter => !filter)}
          />
          <DefaultFilterHandler
            open={filterOn}
            onClose={() => setFilterOn(false)}
          />
        </div>
      </HeroSessionContent>

      {legacyInsights && (
        <LegacyInsightsCard
          showOldReports={showOldReports}
          setShowOldReports={setShowOldReports}
          loading={dailyMetricsLoading}
        />
      )}

      <LocationOverviewGrid
        data={dailyMetrics?.meta}
        loading={dailyMetricsLoading}
      />

      <InsightsOverview
        data={chartData}
        reviews={reviewsOverview}
        showOldReports={showOldReports}
        loading={dailyMetricsLoading}
      />

      <Show condition={!showOldReports}>
        <RowContainer>
          <MultiLineChartCard
            loading={dailyMetricsLoading}
            title="Reviews Average"
            height={430}
            type="bar"
            labels={[
              {
                text: 'Range Average',
                color: '#000000',
                data: getRatingAverage(reviewsOverview?.ratingAvg ?? {}),
              },
              {
                text: 'Reviews Qty.',
                color: '#000000',
                data: chartData?.questionsAndReviews?.data[0]?.total ?? 0,
              },
            ]}
            growthPriority={3.4}
            overrideOptions
            series={chartData?.reviewsAverage?.data || []}
            options={chartData?.reviewsAverage?.options || {}}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={dailyMetricsLoading}
            title="Reviews and questions on Google"
            height={430}
            growthPriority={3.4}
            series={chartData?.questionsAndReviews?.data}
            options={chartData?.questionsAndReviews?.options}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={dailyMetricsLoading}
            title="insights.search.title"
            height={430}
            growthPriority={3.4}
            series={chartData?.searchViews?.data}
            options={chartData?.searchViews?.options}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={dailyMetricsLoading}
            title="insights.maps.title"
            height={430}
            growthPriority={3.4}
            series={chartData?.mapsViews?.data}
            options={chartData?.mapsViews?.options}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={dailyMetricsLoading}
            title="insights.actions.title"
            height={430}
            growthPriority={3.4}
            series={chartData?.customerActions?.data}
            options={chartData?.customerActions?.options}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={loadingNps}
            title="insights.nps.title"
            height={430}
            growthPriority={3.4}
            series={chartData?.nps?.data}
            options={chartData?.nps?.options}
          />
        </RowContainer>
      </Show>

      <Show condition={showOldReports}>
        <RowContainer>
          <MultiLineChartCard
            loading={loadingQuestions || loadingReviews}
            title="Posts on Google"
            height={430}
            growthPriority={3.4}
            series={chartData?.posts?.data}
            options={chartData?.posts?.options}
            legacy={true}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={chartDataLoading}
            title="Where customers view your business on Google"
            height={430}
            growthPriority={3.4}
            series={chartData?.views?.data}
            options={chartData?.views?.options}
            legacy={true}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={chartDataLoading}
            title="How customers search your business"
            growthPriority={3.4}
            series={chartData?.queries?.data}
            options={chartData?.queries?.options}
            height={430}
            legacy={true}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={chartDataLoading}
            title="Customer Actions"
            height={430}
            growthPriority={3.4}
            series={chartData?.aggregatedDaily?.data}
            options={chartData?.aggregatedDaily?.options}
            legacy={true}
          />
        </RowContainer>
        <RowContainer>
          <MultiLineChartCard
            loading={chartDataLoading}
            title="Photos uploaded on Google"
            height={430}
            growthPriority={3.4}
            series={chartData?.photosUploaded?.data}
            options={chartData?.photosUploaded?.options}
            legacy={true}
          />
        </RowContainer>
      </Show>
    </Container>
  )
}

const Wrapper = () => {
  const arrayParam = withDefault(
    {
      encode: array => array,
      decode: array => array?.split(','),
    },
    []
  )
  return (
    <SearchParamsHandlerProvider
      queryParams={{
        states: withDefault(arrayParam, []),
        cities: withDefault(arrayParam, []),
        areas: withDefault(arrayParam, []),
        labels: withDefault(arrayParam, []),
        locations: withDefault(arrayParam, []),
      }}
    >
      <Insights />
    </SearchParamsHandlerProvider>
  )
}
const CreateButton = styled(Button)`
  font-size: 14px;
  height: 32px;
  padding: 5px 15px;
  font-weight: 500;
  color: white;
  background-color: ${props => props.theme.color.primary} !important;
  text-transform: uppercase;
  border-radius: 50px;

  &:hover {
    background-color: ${props => shade(0.2, props.theme.color.primary)};
    color: white;
  }

  svg {
    margin-right: 12px;
  }
`

const BreadcrumbContainer = styled.div`
  display: flex;
  justify-content: space-between;

  @media ${queries.less.sm} {
    flex-direction: column;
    gap: 0.5em;
  }

  .insights-menu-buttons {
    display: flex;
    align-items: center;
    justify-content: start;
    gap: 1em;

    @media ${queries.less.xl} {
      flex-direction: column;

      > div {
        width: 100%;
      }

      > button {
        width: 100%;
      }
    }
  }
`

const HeroSessionContent = styled.div`
  display: flex;
  padding-left: 16px;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;

  h2 {
    color: #767676;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
  }

  .buttons {
    display: flex;
    align-items: stretch;

    gap: 8px;
  }

  & .form-control {
    height: 100% !important;
  }
`

export default Wrapper
